import { useFormContext } from "react-hook-form";
import { useMemo } from "react";
import { isArray, isBoolean, isObject, isString } from "lodash";
import {
  parseDatePickerValue,
} from "@/src/shared/utils/parseDatePickerValue";
import { maskFunctions } from "@/src/shared/utils/mask";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import { formatDate, isValidDate } from "@/src/shared/utils/dateTime";

const safeParce = (value: string): any => {
  if (!value && typeof value !== "boolean" && typeof value !== "number")
    return "";
  try {
    const parsedData = JSON.parse(value);
    return parsedData;
  } catch (e) {
    return value;
  }
};
export const parseVariableToString = (val: any, contextName: string): string => {
  const value = safeParce(val);
  if (isArray(value) && isObject(value[0])) {
    return value.map((item: any) => item.label || item.value).join(", ");
  }
  if (isArray(value) && !isObject(value[0])) {
    return value.join(", ");
  }
  if (isObject(value)) {
    // @ts-ignore
    return value.label || value.value || "";
  }
  if (isBoolean(value)) {
    return value ? "Так" : "Ні";
  }

  if (isString(value)) {
    const dateCandidate = parseDatePickerValue(value);

    if (
      dateCandidate?.isValid &&
      isValidDate(value) &&
      contextName?.toLowerCase().includes("date")
    ) {
      return formatDate(dateCandidate, "dd.MM.yyyy");
    }

    return value;
  }

  return value ?? "";
};

const regex = /{{(.*?)}}/g;

export const extractNames = (inputString: string): string[] => {
  const matches: string[] = [];
  let match: RegExpExecArray | null;

  while ((match = regex.exec(inputString)) !== null) {
    matches.push(match[1]);
  }

  return matches;
};

export const replaceNames = (
    inputString: string,
    replacements: Record<string, string>,
    withoutDefault = false,
    makeBold = false,
): React.ReactNode => {
    if (typeof inputString !== "string") return inputString;

    const isHtml = (str: string) => /<\/?[a-z][\s\S]*>/i.test(str);

    return inputString.split(/{{(.*?)}}/g).map((segment, index) => {
        if (index % 2 === 1) {
            const replacement = replacements[segment] ?? (withoutDefault ? "" : segment);

            if (isHtml(replacement)) {
                return (
                    <span key={index} dangerouslySetInnerHTML={{ __html: replacement }} />
                );
            }

            if (makeBold) {
                return (
                    <span key={index} className="font-semibold">
            {replacement}
          </span>
                );
            }

            return replacement;
        }

        if (isHtml(segment)) {
            return <span key={index} dangerouslySetInnerHTML={{ __html: segment }} />;
        }

        return segment;
    });
};

export const ParsedValue = ({
  value,
  makeBold = false,
  defaultValues,
  maskType,
  style
}: {
  value: string;
  makeBold?: boolean;
  defaultValues?: Record<string, string>;
  maskType?: string;
  style?: any;
}) => {
  const { watch } = useFormContext();
  const contextFields = extractNames(value);
  const formValues = watch(contextFields.map((n) => n.trim()));
  const parsedValue = useMemo(() => {
    const values =
      defaultValues ||
      Object.fromEntries(
        contextFields.map((name, i) => {
          const label = parseVariableToString(formValues[i], name);
          if (maskType === "thousandSeparator") {
            return [name, maskFunctions.thousandSeparator(label)];
          }
          return [name, label];
        }),
      );
    return replaceNames(value, values, true, makeBold);
  }, [contextFields, formValues, value, makeBold]);

  return <span className="whitespace-pre-line" style={style}>{parsedValue}</span>;
};

export const TextWithValue = ({
  text = "",
  valueString = "",
  textFontSize,
  valueFontSize,
  maskType,
  color,
  className,
}: {
  text?: string;
  valueString?: string;
  textFontSize?: number;
  valueFontSize?: number;
  maskType?: string;
  color?: string;
  className?: string;
}) => {
  return (
    <div
      className={cn("flex w-full items-center gap-2", className)}
      style={{ color }}
    >
      <div style={{ fontSize: textFontSize }}>{text}</div>
      <div className="font-semibold" style={{ fontSize: valueFontSize }}>
        <ParsedValue value={valueString} maskType={maskType} />
      </div>
    </div>
  );
};
